import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import Icon from "components/AppIcon";
import {
  ASSESSMENT_STATUS_CHART_COLORS,
  DPAT_BRAND,
  DPAT_CHART,
  tooltipStyle,
} from "../dpatChartTheme";
import DpatNotReviewedDistrictsTable from "./DpatNotReviewedDistrictsTable";

const STATUS_META = [
  { key: "yetToStart", label: "Yet to start", color: DPAT_CHART.slate, icon: "CircleDashed", hint: "Not started" },
  { key: "inProgress", label: "Start", color: DPAT_CHART.navy, icon: "Play", hint: "In progress" },
  { key: "pendingReview", label: "Pending", color: DPAT_CHART.amber, icon: "Clock", hint: "Awaiting QA release" },
  { key: "reviewed", label: "Reviewed", color: DPAT_CHART.violet, icon: "BadgeCheck", hint: "QA reviewed" },
  { key: "completed", label: "Completed", color: DPAT_CHART.emerald, icon: "CheckCircle2", hint: "Released" },
  { key: "closed", label: "Closed", color: DPAT_CHART.teal, icon: "Lock", hint: "Closed" },
];

function ChartCard({ title, subtitle, children, className = "" }) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-[#16325a]/10 bg-gradient-to-b from-white to-[#f7f9fc] p-5 shadow-[0_10px_30px_rgba(22,50,90,0.06)] ${className}`}
    >
      <div className="mb-4">
        <h3 className="text-base font-semibold tracking-tight text-[#16325a]">{title}</h3>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

function StatusMetricCard({ label, value, hint, color, icon, total }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div
      className="relative overflow-hidden rounded-2xl border bg-white p-4 shadow-[0_8px_24px_rgba(22,50,90,0.05)]"
      style={{ borderColor: `${color}33` }}
    >
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }}
      />
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</p>
          <p className="mt-2 text-3xl font-bold tabular-nums text-[#16325a]">{value}</p>
          <p className="mt-1 text-xs text-slate-500">
            {hint} · <span className="font-semibold" style={{ color }}>{pct}%</span>
          </p>
        </div>
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: `${color}18` }}
        >
          <Icon name={icon} size={20} color={color} />
        </div>
      </div>
      <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function ProgressTrack({ label, value, total, color }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div className="rounded-xl border border-slate-100 bg-white/80 px-3 py-2.5">
      <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
        <span className="flex items-center gap-2 font-medium text-slate-600">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
          {label}
        </span>
        <span className="font-semibold tabular-nums text-[#16325a]">
          {value}
          <span className="ml-1 font-normal text-slate-400">({pct}%)</span>
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

const DpatAssessmentWorkflowSection = ({
  summary,
  reportingYear,
  loading,
  assessmentRows = [],
}) => {
  const emptySummary = !summary || summary.total === 0;

  const pieData = useMemo(() => summary?.statusPieChart || [], [summary]);
  const barData = useMemo(() => summary?.pipelineBarChart || [], [summary]);
  const regionChartData = useMemo(
    () =>
      (summary?.regionBreakdown || []).map((region) => {
        const name = String(region.regionName || region.regionKey || "").trim();
        return {
          region: /region$/i.test(name) ? name : name ? `${name} Region` : name,
          "Yet to start": region.yetToStart,
          Start: region.start,
          Pending: region.pending,
          Reviewed: region.reviewed,
          Completed: region.completed,
          Closed: region.closed,
        };
      }),
    [summary],
  );
  const regionChartHeight = Math.max(420, regionChartData.length * 38);

  if (loading) {
    return (
      <section className="mb-8 space-y-5">
        <DpatNotReviewedDistrictsTable
          rows={assessmentRows}
          loading={loading}
          reportingYear={reportingYear}
        />
        <div className="h-8 w-72 animate-pulse rounded-lg bg-slate-200" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-2xl border border-border bg-card" />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="h-80 animate-pulse rounded-2xl border border-border bg-card" />
          <div className="h-80 animate-pulse rounded-2xl border border-border bg-card" />
          <div className="h-80 animate-pulse rounded-2xl border border-border bg-card" />
        </div>
      </section>
    );
  }

  return (
    <section className="mb-8 space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-[#16325a]/10 bg-gradient-to-r from-[#16325a] to-[#1f4a7a] px-5 py-4 text-white shadow-[0_12px_28px_rgba(22,50,90,0.18)] md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-white/70">
            Workflow monitor
          </p>
          <h2 className="mt-1 text-xl font-semibold md:text-2xl">
            Assessment progress — {reportingYear}
          </h2>
          <p className="mt-1 text-sm text-white/75">
            Live view of MMDA movement from start through release and closure.
          </p>
        </div>
        {!emptySummary ? (
          <div className="flex flex-wrap gap-3">
            <div className="rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Started</p>
              <p className="text-lg font-bold tabular-nums">
                {summary.startedCount}
                <span className="text-sm font-normal text-white/70"> / {summary.total}</span>
              </p>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Completed rate</p>
              <p className="text-lg font-bold tabular-nums">{summary.completedPct}%</p>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Ready to release</p>
              <p className="text-lg font-bold tabular-nums">{summary.qaReleaseEligible}</p>
            </div>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {STATUS_META.map((meta) => (
          <StatusMetricCard
            key={meta.key}
            label={meta.label}
            value={summary?.[meta.key] ?? 0}
            hint={meta.hint}
            color={meta.color}
            icon={meta.icon}
            total={summary?.total ?? 0}
          />
        ))}
      </div>

      {emptySummary ? (
        <div className="rounded-2xl border border-dashed border-[#16325a]/20 bg-[#f7f9fc] px-4 py-12 text-center text-sm text-slate-500">
          Assessment workflow charts will appear once district statuses load for {reportingYear}.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <ChartCard
              title="Overall pipeline"
              subtitle="Share of districts at each workflow stage"
            >
              <div className="space-y-2.5">
                <ProgressTrack
                  label="Yet to start"
                  value={summary.yetToStart}
                  total={summary.total}
                  color={ASSESSMENT_STATUS_CHART_COLORS.none}
                />
                <ProgressTrack
                  label="Start"
                  value={summary.inProgress}
                  total={summary.total}
                  color={ASSESSMENT_STATUS_CHART_COLORS.Start}
                />
                <ProgressTrack
                  label="Pending"
                  value={summary.pendingReview}
                  total={summary.total}
                  color={ASSESSMENT_STATUS_CHART_COLORS.Pending}
                />
                <ProgressTrack
                  label="Reviewed"
                  value={summary.reviewed}
                  total={summary.total}
                  color={ASSESSMENT_STATUS_CHART_COLORS.Reviewed}
                />
                <ProgressTrack
                  label="Completed"
                  value={summary.completed}
                  total={summary.total}
                  color={ASSESSMENT_STATUS_CHART_COLORS.Completed}
                />
                <ProgressTrack
                  label="Closed"
                  value={summary.closed}
                  total={summary.total}
                  color={ASSESSMENT_STATUS_CHART_COLORS.Closed}
                />
              </div>
            </ChartCard>

            <ChartCard
              title="Status mix"
              subtitle={`Distribution of ${summary.total} districts`}
            >
              <div className="relative h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="46%"
                      innerRadius={58}
                      outerRadius={90}
                      paddingAngle={3}
                      stroke="#fff"
                      strokeWidth={2}
                    >
                      {pieData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={
                            ASSESSMENT_STATUS_CHART_COLORS[entry.name]
                            || ASSESSMENT_STATUS_CHART_COLORS[entry.key]
                            || DPAT_CHART.slate
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend verticalAlign="bottom" height={40} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-x-0 top-[38%] text-center">
                  <p className="text-xs uppercase tracking-wide text-slate-400">Districts</p>
                  <p className="text-2xl font-bold tabular-nums text-[#16325a]">{summary.total}</p>
                </div>
              </div>
            </ChartCard>

            <ChartCard
              title="Stage counts"
              subtitle="Absolute district counts by status"
            >
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} margin={{ top: 12, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(22,50,90,0.08)" vertical={false} />
                    <XAxis
                      dataKey="stage"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      interval={0}
                      angle={-18}
                      textAnchor="end"
                      height={58}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      cursor={{ fill: "rgba(22,50,90,0.04)" }}
                    />
                    <Bar dataKey="count" radius={[8, 8, 4, 4]} maxBarSize={42}>
                      {barData.map((entry) => (
                        <Cell
                          key={entry.stage}
                          fill={
                            ASSESSMENT_STATUS_CHART_COLORS[entry.stage]
                            || ASSESSMENT_STATUS_CHART_COLORS[entry.fillKey]
                            || DPAT_BRAND
                          }
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>
          </div>

          {regionChartData.length > 0 ? (
            <div
              data-pdf-break-after
              className="rounded-xl border border-border bg-card p-4 shadow-sm md:p-5"
            >
              <h3 className="text-base font-semibold text-foreground">Regional assessment movement</h3>
              <p className="mt-1 mb-4 text-sm text-muted-foreground">
                How districts in each region are progressing through the DPAT workflow
              </p>
              <div style={{ height: regionChartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={regionChartData}
                    layout="vertical"
                    margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(22,50,90,0.08)" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                    <YAxis
                      type="category"
                      dataKey="region"
                      width={210}
                      interval={0}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                    <Bar dataKey="Yet to start" stackId="a" fill={ASSESSMENT_STATUS_CHART_COLORS.none} />
                    <Bar dataKey="Start" stackId="a" fill={ASSESSMENT_STATUS_CHART_COLORS.Start} />
                    <Bar dataKey="Pending" stackId="a" fill={ASSESSMENT_STATUS_CHART_COLORS.Pending} />
                    <Bar dataKey="Reviewed" stackId="a" fill={ASSESSMENT_STATUS_CHART_COLORS.Reviewed} />
                    <Bar dataKey="Completed" stackId="a" fill={ASSESSMENT_STATUS_CHART_COLORS.Completed} />
                    <Bar
                      dataKey="Closed"
                      stackId="a"
                      fill={ASSESSMENT_STATUS_CHART_COLORS.Closed}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}
        </>
      )}

      <DpatNotReviewedDistrictsTable
        rows={assessmentRows}
        loading={loading}
        reportingYear={reportingYear}
      />
    </section>
  );
};

export default DpatAssessmentWorkflowSection;
